import { Injectable } from "@angular/core";
import { Platform } from '@ionic/angular';
import * as signalR from "@microsoft/signalr";
import { nextTick } from "process";
import { BehaviorSubject, Subscription } from "rxjs";
import { map } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { LangsRepository } from "./langs.repository";
import { LeadClientRepositoryBUT } from "./lead.clients.repository.but";
import { NotificationService } from "./notification.service";
import { WhatsAppChatType } from "../Enums/whatsappChatType.enum";
import { ILang } from "../model/lang.interface";
import { ChatMessageType } from "../model/leadClients/lead.client.whatsapp.chatmessagetype";
import { LeadClientWhatsappReceivedMessage } from "../model/leadClients/lead.client.whatsapp.receivedmessage";
import { LeadClientWhatsappMessage } from "../model/leadClients/lead.client.whatsapp.message";
import { PoviderType } from "../model/leadClients/lead.client.whatsapp.providertype";
import { User } from "../model/user.model";
import { Result } from "../model/result";

@Injectable()
export class WhatsappService {
    private isConnected = false;
    private signalRConnectionSubject = new BehaviorSubject<signalR.HubConnection>(null);
    public signalRConnection$ = this.signalRConnectionSubject.asObservable();
    public signalRConnection: signalR.HubConnection;
    public signalRConnectionSubscription: Subscription;
    private receivedMessageSubject = new BehaviorSubject<LeadClientWhatsappReceivedMessage>(null);
    public receivedMessage$ = this.receivedMessageSubject.asObservable();
    public receivedMessage: LeadClientWhatsappReceivedMessage;
    public receivedMessageSubscription: Subscription;
    private signalRConnectionCloseRequested = false;
    private token = "";
    private chatType = WhatsAppChatType.Client;
    private readonly messageFileErrorPlaceholder = "__messagefile_error__";

    constructor(
        protected langsRepository: LangsRepository,
        protected leadClientRepository: LeadClientRepositoryBUT,
        public readonly notificationService: NotificationService,
        private platform: Platform
    ) { }

    get currentLang(): ILang { return this.langsRepository.current };

    setSignalRConnection(signalRConnection: signalR.HubConnection) {
        this.signalRConnectionSubject.next(signalRConnection);
    }

    setReceivedMessage(receivedMessage: LeadClientWhatsappReceivedMessage) {
        this.receivedMessageSubject.next(receivedMessage);
    }

    public setToken(token: string): void {
        this.token = token;
    }

    public setWhatsAppChatType(chatType: WhatsAppChatType): void {
        this.chatType = chatType;
    }

    public async startConnection(userId: number) {
        try {
            console.log('startConnection', userId);
            this.signalRConnectionCloseRequested = false;
            this.signalRConnection.start().then(async () => {
                if (this.signalRConnection) {
                    await this.signalRConnection.invoke('ConnectUser', userId);
                    try {
                        if (sessionStorage.getItem('crm-app-impersonation-session')) {
                            await this.signalRConnection.invoke('MarkImpersonationSession');
                            sessionStorage.removeItem('crm-app-impersonation-session');
                        }
                    } catch (_) { }
                    this.isConnected = true;
                }
            }, err => {
                console.error('start signalRConnection rejected', err)
            }).catch(err => {
                console.error('start signalRConnection error', err)
            });
        } catch (err) {
            console.error('startConnection error', err);
            setTimeout(() => {
                this.startConnection(userId);
            }, 5000);
        }
    };

    public async subscribe(user: User): Promise<void> {
        await this.platform.ready();

        this.signalRConnectionSubscription = this.signalRConnection$.subscribe(async (con: signalR.HubConnection) => {
            if (con !== null && !this.signalRConnection) {
                this.signalRConnection = con;
                await this.startConnection(user.id);
            }
        });

        const connectionOptions = { 
            transport: signalR.HttpTransportType.WebSockets, 
            skipNegotiation: true 
        };
        let connection = new signalR.HubConnectionBuilder()
            .withUrl(`${environment.baseFixUrl}chatHub`, connectionOptions)
            .configureLogging(signalR.LogLevel.Information)
            .withAutomaticReconnect()
            .build();
        
        // Handle ForceLogout notification from backend
        connection.on("ForceLogout", (data: any) => {
            console.log("Received ForceLogout notification", data);
            window.dispatchEvent(new CustomEvent('force-logout', { detail: data }));
        });

        connection.on("CompanySwitched", (companyId: number, jwt?: string, isImpersonation?: boolean) => {
            if (jwt) {
                const isInIframe = (parent !== window);
                const hostname = isInIframe ? document.referrer : window.location.hostname;
                const authKey = `fxa-${hostname}`;
                try {
                    const raw = localStorage.getItem(authKey);
                    if (raw) {
                        const data = JSON.parse(raw);
                        data.jwt = jwt;
                        localStorage.setItem(authKey, JSON.stringify(data));
                    }
                } catch (_) { }
            }
            if (isImpersonation) {
                try { sessionStorage.setItem('crm-app-impersonation-session', '1'); } catch (_) { }
            }
            window.location.reload();
        });
        
        connection.onclose(async (error?: Error) => {
            if (!this.signalRConnectionCloseRequested) {
                await this.startConnection(user.id);
            }
            if (error) {
                console.error("error in connection.onclose", error);
            }
        });
        this.setSignalRConnection(connection);
    }

    public unsubscribe(user: User): void {
        if (this.signalRConnection) {
            if (this.signalRConnection.state == signalR.HubConnectionState.Connected) {
                this.signalRConnection.invoke('DisconnectUser', user.id)
                    .finally(() => {
                        if (this.signalRConnection) {
                            this.signalRConnectionCloseRequested = true;
                            if (this.isConnected) {
                                this.signalRConnection.stop().then(_ => {
                                    this.signalRConnection = null;
                                    this.setSignalRConnection(null);
                                    this.isConnected = false;
                                });
                            }
                        }
                    });
            } else {
                this.signalRConnection = null;
                this.setSignalRConnection(null);
            }

            if (this.signalRConnectionSubscription) {
                this.signalRConnectionSubscription.unsubscribe();
            }
            this.setReceivedMessage(null);
            this.receivedMessage = null;
            if (this.receivedMessageSubscription) {
                this.receivedMessageSubscription.unsubscribe();
            }
        }
    }

    public formatWhatsappMessage(msg: LeadClientWhatsappMessage) {
		if (!msg.message) {
			return "";
		}
		msg.message = msg.message.replace(/\n/g, "<br />");

		const urlRegex = /\b((https?:\/\/\S+(?:png|jpe?g|gif)\S*)|(https?:\/\/api\-eu\.nexmo\.com\/v3\/media\/\S+))\b/gi;
		const fileRegex = /\b(leadClient\/getMessageFile\/(\w+)\/)(.+?\.([a-zA-Z]\w+\b|$))/i;
        const fileNameRegex = /[^\/]+$/;

		let datePast = new Date();
		const createDate = new Date(msg.createDate);
		if (urlRegex.test(msg.message)) {
			datePast.setDate(datePast.getDate() - 90);
			if (createDate <= datePast) {
				return msg.message.replace(urlRegex, `<b>${this.currentLang.words["image-deleted"]}</b>`);
			}
			return msg.message
                // replace uploaded images to alias with https (instead of http)
                .replace("http://prod2-clp-fixdigital.s3-website-eu-west-1.amazonaws.com", "https://lpc.fixdigital.co.il")
                .replace(urlRegex, `<a href='$1' target='_blank'><img src='$1' alt='image' /></a>`);
		}
		if (fileRegex.test(msg.message)) {
			datePast.setDate(datePast.getDate() - 90);

			if (createDate <= datePast || msg.message.includes('/deleted/')) {
				return msg.message.replace(fileRegex, `<b>${this.currentLang.words["image-deleted"]}</b>`);
			}
			if (!msg.fileUrl) {
				let filename = msg.filename ? encodeURIComponent(msg.filename) : encodeURIComponent(msg.message.match(fileRegex)[3]);
				let url = msg.message.match(fileRegex)[1] + filename;
				var getMessageFileMethodPromise = this.chatType === WhatsAppChatType.Client
						? this.leadClientRepository.getMessageFile(url)
						: this.leadClientRepository.getMessageFileWithCrmToken(this.token, url);
				getMessageFileMethodPromise.then(blob => {
					msg.file = blob;
					let fileUrl = window.URL.createObjectURL(blob);
					msg.fileUrl = fileUrl;
				})
				.catch(reason => {
					console.log('Error in getMessageFileMethodPromise: ', reason, url, msg.messageContentType, msg.message);
					// Stop the retry loop and show the original message text instead.
					msg.fileUrl = this.messageFileErrorPlaceholder;
					msg.imgSrc = null;
				});

				msg.fileUrl = '/loading.svg';
			}

			// If loading failed, render the original message (including the getMessageFile/... text)
			// and do not try to request again.
			if (msg.fileUrl === this.messageFileErrorPlaceholder) {
				msg.imgSrc = null;
				// Wrap with a light red background to make the error visible to the user.
				return `<div style="background-color:#ffe6e6; padding:6px 8px; border-radius:6px; display:inline-block;">${msg.message}</div>`;
			}

			if (msg.messageContentType == "video") {
				return msg.message.replace(fileRegex, `<video controls><source src='${msg.fileUrl}'> </video>`);
			}

			if (msg.messageContentType == "audio") {
				return msg.message.replace(fileRegex, `<audio controls><source src='${msg.fileUrl}'> </audio>`);
			}

			if (msg.messageContentType == "file") {
				fileRegex.lastIndex = 0;
				const match = fileRegex.exec(msg.message);
                let filename = "";
                if (msg.filename) {
                    filename = msg.filename;
                } else {
                    const matchFilename = fileNameRegex.exec(match[0]);
                    filename = match ? matchFilename[0] : "unknown";
                }
				return msg.message.replace(fileRegex, this.createFileMessageHtml(filename, msg.fileUrl));
			}

			nextTick(() => {
				msg.imgSrc = msg.fileUrl;
			});
			return msg.message.replace(fileRegex, "");
		}

		if (msg.adPreview) {
			return `${msg.message} ${msg.adPreview}`;
		}

        const anchorRegex = /(<a[^>]*>)([^<]+)(<\/a>)/gi;
        if (msg.messageType === ChatMessageType.Template && msg.filename && anchorRegex.test(msg.message)) {
            return msg.message.replace(anchorRegex, `$1${msg.filename}$3`);
        }

		return msg.message;
	}

    private createFileMessageHtml(filename: string, fileUrl: string): string {
		return "<div class='file'>" +
			"<span class='file-icon'></span>" +
			"<div class='meta'>" +
			`<div class='title'>${filename}</div>` +
			`<a target="_blank" href='${fileUrl}' class="download" download=${filename}>Download</a>` +
			"</div>" +
			"</div>"
	}

	// region Sending messages
	private mediaTypes = ['video', 'audio', 'image'];

	private mapFileType(file: File): string {
		const fileType = file.type;
		for (const category of this.mediaTypes) {
			if (fileType.startsWith(`${category}/`)) {
				return category;
			}
		}
		return "file";
	}

	public sendReplyMessage(params: {
		token?: string;
		leadClientId: number;
		userId: number;
		messageText: string;
		filesToUpload?: File[] | null;
		selectedPhone?: string;
		senderOrigin?: string;
		senderDestination?: string;
	}) {
		const request$ = params.token
			? this.leadClientRepository.sendReplyWhatsappMessageWithCrmToken(
				params.token,
				params.leadClientId,
				params.messageText,
				params.userId,
				params.filesToUpload,
				params.selectedPhone
			)
			: this.leadClientRepository.sendReplyWhatsappMessage(
				params.leadClientId,
				params.messageText,
				params.userId,
				params.filesToUpload,
				params.selectedPhone
			);

		return request$.pipe(
			map((response: any) => {
				const result: Result = response.data;
				if (!result || !result.success) {
					throw new Error((result && result.message) || "Send message failed");
				}

				const senderOrigin = params.senderOrigin || "";
				const senderDestination = params.senderDestination || "";
				const hasFiles = !!(params.filesToUpload && params.filesToUpload.length > 0);

				if (hasFiles) {
					const uploadData: any = result.data || {};
					const fileUrls: string[] = Array.isArray(uploadData.filesUrl) ? uploadData.filesUrl : [];
					const contentMessageType = params.filesToUpload.length === 1 ? this.mapFileType(params.filesToUpload[0]) : "file";

					return fileUrls.map((fileUrl: string) =>
						new LeadClientWhatsappMessage(
							0, PoviderType.Whatsapp, new Date(), params.leadClientId, ChatMessageType.Reply, fileUrl, senderOrigin, senderDestination, params.userId, contentMessageType
						)
					);
				}

				return [
					new LeadClientWhatsappMessage(
						0, PoviderType.Whatsapp, new Date(), params.leadClientId, ChatMessageType.Reply, params.messageText, senderOrigin, senderDestination, params.userId, "text"
					)
				];
			})
		);
	}
	// endregion

	// region UI helpers - textarea Enter/newline handling
	/**
	 * Compute handling for Enter in a textarea.
	 * - Plain Enter => send
	 * - Any modifier (Ctrl/Shift/Alt/Meta) + Enter => insert newline at caret
	 */
	public handleTextareaEnterKey(
		event: KeyboardEvent,
		currentText: string
	): { updatedText: string | null; newCaret: number | null; shouldSend: boolean; preventDefault: boolean } {
		if (event.key !== "Enter") {
			return { updatedText: null, newCaret: null, shouldSend: false, preventDefault: false };
		}
		const textarea = event.target as HTMLTextAreaElement;
		if (event.ctrlKey || event.shiftKey || event.altKey || event.metaKey) {
			// Insert newline at caret (replace selection if any)
			const selectionStart = textarea.selectionStart || 0;
			const selectionEnd = textarea.selectionEnd || 0;
			const before = currentText.slice(0, selectionStart);
			const after = currentText.slice(selectionEnd);
			const updatedText = `${before}\n${after}`;
			return {
				updatedText,
				newCaret: selectionStart + 1,
				shouldSend: false,
				preventDefault: true
			};
		}
		// Plain Enter => send
		return { updatedText: null, newCaret: null, shouldSend: true, preventDefault: true };
	}
	// endregion

	public getReferralUrl(referralData: string): string | null {
		if (!referralData) {
			return null;
		}
		
		try {
			const referral = JSON.parse(referralData);
			// WhatsApp Referral может содержать source_url или sourceUrl
			return referral.source_url || referral.sourceUrl || null;
		} catch (e) {
			console.warn('Failed to parse referralData:', e);
			return null;
		}
	}
}

